import { useEffect } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'hooks/store';
import {
  setBreadcrumbsAction,
  breadcrumbsSelector,
  BreadcrumbItem,
} from 'state/slices/breadcrumb';
import { ROUTES } from 'constants/routes';

interface BreadcrumbNavigator {
  push(breadcrumb: BreadcrumbItem): void
  replace(incomingCrumbs: BreadcrumbItem[], range?: number): void
  toCrumb(crumbIndex: number): void
  toUrl(url: string): void
  toPrevious(): void
  current(): BreadcrumbItem[]
}

export const useRootPage = (rootBreadcrumb: BreadcrumbItem): void => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setBreadcrumbsAction([rootBreadcrumb]));
  }, []);
};

export const useBreadcrumbNavigation = (): BreadcrumbNavigator => {
  const dispatch = useDispatch();
  const curBreadcrumbs = useSelector(breadcrumbsSelector);
  const history = useHistory();

  const push = (breadcrumb: BreadcrumbItem) => {
    const updatedBreadcrumbs = [
      ...curBreadcrumbs,
      breadcrumb,
    ];
    dispatch(setBreadcrumbsAction(updatedBreadcrumbs));
    history.push(breadcrumb.url);
  };

  const replace = (incomingCrumbs: BreadcrumbItem[], range?: number) => {
    // if (range < 1) {
    //   throw new Error('Invalid replace range');
    // }
    const parsedRange = range === undefined ? 0 : range;
    const finalRange = parsedRange > curBreadcrumbs.length ? curBreadcrumbs.length : parsedRange;
    const updatedBreadcrumbs = [
      ...curBreadcrumbs.slice(0, curBreadcrumbs.length - finalRange),
      ...incomingCrumbs,
    ];
    dispatch(setBreadcrumbsAction(updatedBreadcrumbs));

    // TODO: append new url
    history.push(updatedBreadcrumbs[updatedBreadcrumbs.length - 1]?.url || ROUTES.root);
  };

  const toCrumb = (crumbIndex: number) => {
    const updatedBreadcrumbs = curBreadcrumbs.slice(0, crumbIndex + 1);
    dispatch(setBreadcrumbsAction(updatedBreadcrumbs));
    history.push(updatedBreadcrumbs[updatedBreadcrumbs.length - 1]?.url || ROUTES.root);
  };

  const toUrl = (url: string) => {
    const urlIndex = curBreadcrumbs.findIndex(
      (crumb) => crumb.url.toLowerCase() === url.toLowerCase(),
    );
    if (urlIndex === -1) return; // TODO: maybe clear breadcrumbs and go to fixed URL
    // if (urlIndex === -1) {
    //   // New URL, clean breadcrumbs and directly push it
    //   const updatedBreadcrumbs = curBreadcrumbs.slice(0, urlIndex + 1);
    //   dispatch(setBreadcrumbsAction(updatedBreadcrumbs));
    //   history.push(updatedBreadcrumbs[updatedBreadcrumbs.length - 1]?.url || ROUTES.root);
    // };
    const updatedBreadcrumbs = curBreadcrumbs.slice(0, urlIndex + 1);
    dispatch(setBreadcrumbsAction(updatedBreadcrumbs));
    history.push(updatedBreadcrumbs[updatedBreadcrumbs.length - 1]?.url || ROUTES.root);
  };

  const toPrevious = () => {
    const breadcrumbsCount = curBreadcrumbs.length;
    toCrumb(breadcrumbsCount - 2);
  };

  const current = () => curBreadcrumbs;

  return {
    push,
    replace,
    toCrumb,
    toUrl,
    toPrevious,
    current,
  };
};
